import { bind, classNames, Component, h, signal, tag } from "omi";
import throttle from "lodash-es/throttle";

import { useLayoutContainer } from "@/components/base/nw-draggable-panel/hooks";
import { getIconForCategory, getUrlForProject } from "@/components/nw-nav/utils";
import { Task, TaskStatus } from "@/api";
import { NWIcon, ProgressState } from "@/components";
import { Store } from "@/store";
import { tailwind } from "@/tailwind";

type Props = {
  state: ProgressState;
  isActive: boolean;
  task: Task;
  first?: boolean;
  last?: boolean;
  single?: boolean;
};

@tag("bar-segment")
export class BarSegment extends Component<Props> {
  static css = [tailwind, ":host { width: 100%; }"];
  private throttledHandleScroll = throttle(this.handleScroll, 10);
  private progress = signal<number>(0);
  private layoutContainer = useLayoutContainer();

  @bind
  installed() {
    if (this.props.isActive) {
      this.layoutContainer.onRegister(ref =>
        ref.addEventListener("scroll", this.throttledHandleScroll, { passive: true }),
      );
    }
  }

  @bind
  uninstall() {
    this.layoutContainer.ref.value?.removeEventListener("scroll", this.throttledHandleScroll);
  }

  @bind
  private handleScroll() {
    if (
      !this.props.isActive ||
      !Store.project.content.trackContent.signal.data.value ||
      !this.layoutContainer.ref.value
    ) {
      return;
    }

    const container = this.layoutContainer.ref.value;
    const scrollTop = container.scrollTop;
    const scrollHeight = container.scrollHeight - container.clientHeight;
    const scrollPercent = (scrollTop / scrollHeight) * 100;

    this.progress.value = Math.max(Math.min(scrollPercent, 100), 0);
  }

  private getColorsForState(state: ProgressState) {
    switch (state) {
      case ProgressState.NotStarted:
        return { bg: "bg-secondary", fg: "bg-secondary", border: "active:border-brand-300", activeBorder: "" };
      case ProgressState.InProgress:
        return {
          bg: "bg-brand-50",
          fg: "bg-leather",
          border: "border-brand-200 active:border-leather focus:border-leather",
          activeBorder: "!border-leather",
        };
      case ProgressState.InReview:
        return {
          bg: "bg-secondary",
          fg: "bg-success-100 border border-success-300",
          border: `active:border-success-300 focus:border-success-300`,
          activeBorder: "!border-success-300",
        };
      case ProgressState.Complete:
        return {
          bg: "bg-success-400",
          fg: "bg-success-400",
          border: `active:border-success-400 focus:border-success-400`,
          activeBorder: "!border-success-300",
        };
    }
  }

  @bind
  private handleClick() {
    window.location.assign(getUrlForProject(this.props.task.id));
  }

  private getDisplayForTaskStatus(status: TaskStatus, isActive: boolean) {
    switch (status) {
      case "COMPLETE":
        return "Complete";
      case "IN_REVIEW":
        return "In Review";
      case "INCOMPLETE":
        return isActive ? "Current project" : "Not complete";
      default:
        return "Current project";
    }
  }

  render({ state = ProgressState.NotStarted, task, isActive, first = false, last = false, single = false }: Props) {
    const colors = this.getColorsForState(state);

    return (
      <div
        onClick={this.handleClick}
        class={classNames(
          "relative group cursor-pointer py-1.5 px-1.5 animate-fadeIn w-full border border-transparent hover:border-gray-300 rounded-[4px] transition-all",
          colors.border,
          { [colors.activeBorder]: isActive && !single },
        )}
      >
        <div class={classNames("progress-bar w-full h-2 rounded-sm transition-all", colors.bg)}>
          <div
            class={classNames("h-full rounded-sm", colors.fg)}
            style={`width: ${Math.min(this.progress.value, 100)}%`}
          ></div>
        </div>

        <div
          class={classNames(
            "bg-white border border-gray-200 py-3 px-4 rounded-lg space-y-3 animate-fadeInFast group-hover:block hidden z-50 max-w-xs w-full min-w-48 sm:min-w-60 md:min-w-80 lg:min-w-96 shadow-md top-6 absolute",
            {
              "translate-x- left-0": first,
              "-translate-x-full left-full": last,
              "-translate-x-1/2 left-1/2": !last && !first,
            },
          )}
        >
          <div class="py-0.5 px-1.5 rounded-md space-x-1.5 flex items-center border border-gray-300 w-fit">
            <div class={classNames("w-2 h-2 rounded-full", colors.fg)}></div>
            <p class="text-xs text-secondary">{this.getDisplayForTaskStatus(task.status, isActive)}</p>
          </div>
          <div>
            <h2 class="font-bold text-lg text-primary">{task.name}</h2>
            <p class="text-secondary text-xs">{task.description}</p>
          </div>
          <div class="flex items-center space-x-1 text-quaternary">
            <NWIcon name={getIconForCategory(task.category)!} width={12} height={12}></NWIcon>
            <p class="text-xs">{task.category}</p>
          </div>
        </div>
      </div>
    );
  }
}
